import { useState } from 'react'
import { AffiliatesService } from '@/services/affiliates'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogBody,
    DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'
import toastService from '@/services/toast/service'

interface AddReferralModalProps {
    affiliateId: string
    affiliateName: string
    open: boolean
    onOpenChange: (open: boolean) => void
    onSuccess: () => void
}

export function AddReferralModal({ affiliateId, affiliateName, open, onOpenChange, onSuccess }: AddReferralModalProps) {
    const [email, setEmail] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleOpenChange = (next: boolean) => {
        if (!next) setEmail('')
        onOpenChange(next)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsSubmitting(true)

        try {
            await AffiliatesService.addReferral(affiliateId, email.trim())
            toastService.success('Referral added')
            onSuccess()
            handleOpenChange(false)
        } catch (err: any) {
            // The api client already toasts server error messages
            if (!err.response?.data?.message) {
                toastService.error("Couldn't add referral")
            }
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-[425px]">
                <form onSubmit={handleSubmit}>
                    <DialogHeader>
                        <DialogTitle>Add Referral</DialogTitle>
                        <DialogDescription>
                            Attribute an existing account to {affiliateName} by its owner's email.
                        </DialogDescription>
                    </DialogHeader>

                    <DialogBody className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="referral-email">Account Email</Label>
                            <Input
                                id="referral-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="user@example.com"
                                disabled={isSubmitting}
                                autoFocus
                            />
                        </div>
                    </DialogBody>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => handleOpenChange(false)}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSubmitting || !email.trim()}>
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Adding...
                                </>
                            ) : (
                                'Add Referral'
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}
